import { CullingInfoRecord, PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog/types"
import { PhotoId } from "@/store/undoable-state"
import { debounced } from "@/utils/debounce"
import { PubSub } from "@/utils/pub-sub"
import { uuid } from "@/utils/uuid"
import { Sentry } from "@/telemetry/sentry"

export type ImportingTaskContext = {
	photo: PhotoRecord
	metadata: PhotoMetadataRecord
	extractedPreview?: Blob
	cullingInfo?: CullingInfoRecord
}

export type ImportingTaskPurpose =
	| "fast-preview"
	| "slow-preview-foreground"
	| "extract-culling-info"
	| "extract-scoring-info"

interface ImportingTask {
	id: PhotoId
	handler: () => Promise<void>
	purpose: ImportingTaskPurpose
}

type QueueEvent = "startQueue" | "clearQueue" | "updateQueue" | "completeQueue"

// TODO: combine with ProcessingQueue using composition
export class ImportingQueue {
	public tasks: Map<string, ImportingTask>

	public queueEvents = new PubSub<QueueEvent>()

	public currentTask: string | null = null

	private taskPriorities: Map<PhotoId, number>
	private taskOrder: string[]

	public remainingTasks = 0
	public totalTasks = 0

	private pauseResolvers: Array<(value: unknown) => void> = []

	private isProcessing = false

	public constructor() {
		this.tasks = new Map()
		this.taskPriorities = new Map()
		this.taskOrder = []
		this.isProcessing = false
	}

	public setTaskPriorities(priorities: Map<PhotoId, number>) {
		this.taskPriorities = priorities
		this.orderTasksDebounced()
	}

	public setTaskPriority(id: PhotoId, offset: number = 1000) {
		const currentPriority = this.taskPriorities.get(id) ?? 0
		this.taskPriorities.set(id, currentPriority + offset)

		this.orderTasksDebounced()
	}

	public async pauseQueue() {
		if (this.tasks.size === 0) {
			return
		}

		if (!this.isProcessing) {
			return
		}

		return new Promise((resolve) => {
			this.pauseResolvers.push(resolve)
		})
	}

	public resumeQueue() {
		if (this.tasks.size === 0) {
			return
		}

		if (!this.isProcessing) {
			this.processNextTask()
		}
	}

	@debounced()
	public startQueue() {
		this.orderTasks()

		this.totalTasks = this.tasks.size
		this.remainingTasks = this.tasks.size

		this.queueEvents.publish("startQueue")

		if (!this.isProcessing) {
			this.processNextTask()
		}
	}

	public addTask(task: ImportingTask) {
		const key = uuid()
		this.tasks.set(key, task)

		this.startQueue()
	}

	public clearTasks(options?: { preserve?: ImportingTaskPurpose[] }) {
		this.queueEvents.publish("clearQueue")

		// Optionally preserve certain task types (e.g., foreground tasks)
		const preservePurposes = options?.preserve
		if (preservePurposes && preservePurposes.length > 0) {
			const preserveSet = new Set(preservePurposes)
			for (const [key, task] of this.tasks.entries()) {
				if (!preserveSet.has(task.purpose)) {
					this.tasks.delete(key)
				}
			}
		} else {
			this.tasks.clear()
		}

		// Preserve taskOrder entries for tasks that remain
		this.taskOrder = this.taskOrder.filter((key) => this.tasks.has(key))

		if (this.tasks.size === 0) {
			this.currentTask = null
		}

		this.isProcessing = false

		this.orderTasks()
	}

	private getTaskPriority(key: PhotoId) {
		return this.taskPriorities.get(key) || 0
	}

	@debounced()
	private orderTasksDebounced() {
		this.orderTasks()
	}

	private orderTasks() {
		const purposePriorities: Record<ImportingTaskPurpose, number> = {
			"fast-preview": 1_000,
			"slow-preview-foreground": 5_000_000_000,
			"extract-culling-info": 1_000_000,
			"extract-scoring-info": 300_000,
		}

		const taskOrder = Array.from(this.tasks.keys())

		taskOrder.sort((a, b) => {
			const taskA = this.tasks.get(a)
			const taskB = this.tasks.get(b)

			if (!taskA || !taskB) {
				return 0
			}

			const basePriorityA = purposePriorities[taskA.purpose]
			const basePriorityB = purposePriorities[taskB.purpose]

			const visibilityPriorityA = this.getTaskPriority(taskA.id)
			const visibilityPriorityB = this.getTaskPriority(taskB.id)

			return basePriorityA + visibilityPriorityA - (basePriorityB + visibilityPriorityB)
		})

		this.taskOrder = taskOrder
	}

	private resolvePauseResolvers() {
		for (const resolve of this.pauseResolvers) {
			resolve(true)
		}
		this.pauseResolvers = []
		this.currentTask = null
		this.isProcessing = false
	}

	private async processNextTask() {
		this.isProcessing = true

		if (this.pauseResolvers.length > 0) {
			this.resolvePauseResolvers()
			return
		}

		this.remainingTasks = this.tasks.size
		this.currentTask = this.taskOrder.pop() ?? null

		// Notify of remaining tasks
		this.queueEvents.publish("updateQueue")

		if (this.currentTask == null) {
			// We reached the end of the queue
			this.queueEvents.publish("completeQueue")
			this.isProcessing = false
			return
		}

		const task = this.tasks.get(this.currentTask)

		if (!task) {
			// Task is missing or removed. Continue on to next task
			this.processNextTask()
			return
		}

		// Remove current task from queue
		this.tasks.delete(this.currentTask)

		try {
			await task.handler()
		} catch (e) {
			console.error(
				`Importing queue: Error processing task ${task.purpose}, id: ${task.id}`,
				e,
			)
			Sentry.captureException(e)
		} finally {
			// Success or error. Go to next task
			this.processNextTask()
		}
	}
}
